import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  ShieldCheck,
  Check,
  MapPin,
  // Clock,
  QrCode,
  ArrowRight,
} from 'lucide-react';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import PerkTags from '../components/PerkTag';
import CopyButton from '../components/CopyButton';
import Stamp from '../components/stamps/Stamp';
import ClaimOverlay from '../components/stamps/ClaimOverlay';
import Seo from '../components/Seo';
import { useLocalizedNavigate, useLang } from '../i18n/useLocalizedNav';
import { getLocationBySlug, categoryKey } from '../content/catalog';
import { locationImages, locationHeroAbsolute } from '../utils/images';

const ORIGIN = 'https://passport.aranortravel.com';

const StripePanel: React.FC<{ label: string; className?: string }> = ({
  label,
  className = '',
}) => (
  <div
    className={`relative overflow-hidden bg-warm-200 flex items-center justify-center ${className}`}
    style={{
      backgroundImage:
        'repeating-linear-gradient(135deg, transparent, transparent 16px, rgba(255,255,255,0.45) 16px, rgba(255,255,255,0.45) 32px)',
    }}
  >
    <span className="px-3 py-1 bg-white/80 rounded-md font-heading font-bold text-[10px] tracking-[0.15em] text-warm-500 uppercase">
      {label}
    </span>
  </div>
);

const LocationDetails: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const lang = useLang();
  const navigate = useLocalizedNavigate();
  // Static localized catalog lookup — rendered into prerendered HTML.
  const location = useMemo(
    () => (slug ? getLocationBySlug(lang, slug) ?? null : null),
    [lang, slug],
  );
  const fullAddress = [location?.address, location?.region]
    .filter(Boolean)
    .join(', ');
  const [showClaim, setShowClaim] = useState(false);
  // Current multiplier the user holds for this location's stamp
  // (null = logged in but not yet collected; undefined = not logged in / unknown).
  const [ownedMultiplier, setOwnedMultiplier] = useState<number | null | undefined>(
    undefined,
  );

  // Once we know which stamp this venue grants, look up the user's progress.
  useEffect(() => {
    if (!user || !location) return;
    let alive = true;
    (async () => {
      try {
        const mine = await api.get('/stamps/my');
        if (!alive) return;
        if (Array.isArray(mine)) {
          const hit = mine.find((s) => s.id === location.stampId);
          setOwnedMultiplier(hit?.multiplier ?? null);
        }
      } catch {
        /* leave unknown */
      }
    })();
    return () => {
      alive = false;
    };
  }, [user, location]);

  if (!location)
    return <div className="text-center py-20 text-warm-500">Location not found.</div>;

  const [firstName, ...restName] = location.name.split(/\s+/);

  return (
    <div className="space-y-10">
      <Seo
        title={`${location.name} — Aranor Passport`}
        description={location.metaText}
        image={locationHeroAbsolute(
          location.slug,
          location.imageCount,
          ORIGIN,
        )}
      />
      {/* Breadcrumb */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-warm-500 min-w-0">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 hover:text-warm-800 transition-colors whitespace-nowrap"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('ld_breadcrumb_locations')}
        </button>
        {location.region && (
          <>
            <span aria-hidden>·</span>
            <span className="whitespace-nowrap">{location.region}</span>
          </>
        )}
        <span aria-hidden>·</span>
        <span className="whitespace-nowrap">{t(categoryKey(location.category))}</span>
        <span aria-hidden>·</span>
        <span className="text-warm-800 font-bold truncate min-w-0">
          {location.name}
        </span>
      </div>

      {/* Hero gallery — image #1 is the hero; up to 2 extras fill a
          right-side column. The right column only appears when extras
          exist; the hero spans full width otherwise. */}
      {(() => {
        const images = locationImages(location.slug, location.imageCount);
        const extras = images.slice(1, 3);
        const hasExtras = extras.length > 0;
        return (
          <div
            className={`grid grid-cols-1 gap-4 ${
              hasExtras ? 'lg:grid-cols-3' : ''
            }`}
          >
            <div
              className={`relative rounded-3xl overflow-hidden h-72 md:h-[28rem] ${
                hasExtras ? 'lg:col-span-2' : ''
              }`}
            >
              {images[0] ? (
                <img
                  src={images[0]}
                  alt={location.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <StripePanel
                  label={`Hero · ${location.name}`}
                  className="w-full h-full"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-brand-secondary/80 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="px-3 py-1 bg-white/90 rounded-md font-heading font-bold text-[10px] tracking-[0.15em] text-warm-700 uppercase">
                    {t(categoryKey(location.category))}
                  </span>
                  <PerkTags
                    discountPercent={location.discountPercent}
                    hasGift={location.hasGift}
                  />
                </div>
                <h1 className="font-display text-4xl md:text-6xl text-white leading-tight">
                  {firstName}{' '}
                  <span className="italic text-brand-primary">
                    {restName.join(' ')}
                  </span>
                </h1>
                <p className="flex items-center gap-2 text-warm-100/90 text-sm">
                  <MapPin className="w-4 h-4" />
                  {[location.address, location.region]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
            </div>
            {hasExtras && (
              <div
                // Cap the right column at the hero's height so the whole
                // gallery block stays one row tall, regardless of the
                // images' natural aspect ratios.
                className={`hidden lg:grid gap-4 h-72 md:h-[28rem] ${
                  extras.length === 1 ? 'grid-rows-1' : 'grid-rows-2'
                }`}
              >
                {extras.map((src, i) => (
                  <img
                    key={src}
                    src={src}
                    alt={`${location.name} ${i + 2}`}
                    loading="lazy"
                    className="w-full h-full min-h-0 object-cover rounded-3xl"
                  />
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Two-column on desktop, single column on mobile. The three grid
          children below are explicitly placed on desktop (lead+earn and the
          rest of the body stack down the left 2 columns; the sidebar sits in
          column 3, spanning both rows on the right). On mobile the grid is a
          single column and `order-*` reorders them to: lead+earn → sidebar
          (Earn this stamp + Location) → rest of the body — so the two info
          panels sit directly under "You'll earn". */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Lead + You'll earn — desktop rows 1, cols 1-2. */}
        <div className="order-1 lg:col-span-2 lg:col-start-1 lg:row-start-1 space-y-10">
          <section className="space-y-6">
            <p className="font-heading font-bold text-sm tracking-[0.25em] text-brand-accent uppercase">
              {t('ld_the_experience')}
            </p>
            {location.leadHtml && (
              <div
                className="prose-venue prose-venue--lead text-warm-600 text-lg leading-relaxed"
                dangerouslySetInnerHTML={{ __html: location.leadHtml }}
              />
            )}
          </section>

          {/* You'll earn — placed right after the lead paragraph. */}
          <section className="bg-white border border-warm-200/70 rounded-3xl p-6 flex flex-col sm:flex-row items-center gap-6">
            <Stamp
              name={location.stampName}
              locationName={location.name}
              category={location.category}
              earned
              size={140}
              className="shrink-0"
            />
            <div className="space-y-2 text-center sm:text-left">
              <p className="font-heading font-bold text-xs tracking-[0.2em] text-warm-500 uppercase">
                {t('ld_youll_earn')}
              </p>
              <h3 className="font-display text-3xl text-warm-800">
                {location.stampName}
              </h3>
              <p className="text-warm-500 text-sm leading-relaxed">
                {t('ld_multi_collection')}
              </p>
              {ownedMultiplier === null && (
                <p className="inline-flex items-center gap-2 text-sm font-bold text-warm-600 bg-warm-100 rounded-lg px-3 py-2">
                  {t('ld_not_collected_yet')}
                </p>
              )}
              {typeof ownedMultiplier === 'number' && (
                <p className="inline-flex items-center gap-2 text-sm font-bold text-brand-secondary bg-brand-primary/20 rounded-lg px-3 py-2">
                  <Check className="w-4 h-4 text-brand-success shrink-0" />
                  {t('ld_already_have', {
                    n: ownedMultiplier,
                    m: ownedMultiplier + 1,
                  })}
                </p>
              )}
            </div>
          </section>
        </div>

        {/* Rest of the body — everything after the lead paragraph. Desktop:
            row 2, cols 1-2 (below the lead+earn block). Mobile: last. */}
        {location.bodyHtml && (
          <div
            className="order-3 lg:col-span-2 lg:col-start-1 lg:row-start-2 prose-venue text-warm-600 text-lg leading-relaxed"
            dangerouslySetInnerHTML={{ __html: location.bodyHtml }}
          />
        )}

        {/* Sidebar — desktop column 3, spanning both rows on the right.
            Mobile: directly under "You'll earn". */}
        <div className="order-2 lg:order-none lg:col-start-3 lg:row-start-1 lg:row-span-2 space-y-6">
          {/* Verify panel */}
          <div className="bg-brand-secondary text-white rounded-3xl p-7 space-y-5">
            <p className="font-heading font-bold text-xs tracking-[0.2em] text-brand-primary uppercase">
              {t('ld_earn_this_stamp')}
            </p>
            {user ? (
              <>
                <h3 className="font-display text-3xl text-brand-primary">
                  {t('ld_ready_to_collect')}
                </h3>
                <p className="text-warm-500 text-sm leading-relaxed">
                  {t('ld_generate_qr_loggedin')}
                </p>
                <button
                  onClick={() => setShowClaim(true)}
                  className="w-full inline-flex items-center justify-center gap-2 py-4 bg-brand-primary text-brand-secondary font-heading font-bold rounded-xl hover:bg-brand-accent transition-all"
                >
                  <QrCode className="w-5 h-5" />
                  {t('ld_show_qr')}
                </button>
              </>
            ) : (
              <>
                <h3 className="font-display text-3xl text-brand-primary">
                  {t('ld_already_on_site')}
                </h3>
                <p className="text-warm-500 text-sm leading-relaxed">
                  {t('ld_generate_qr_loggedout')}
                </p>
                <button
                  onClick={() => navigate('/login')}
                  className="w-full inline-flex items-center justify-center gap-2 py-4 bg-brand-primary text-brand-secondary font-heading font-bold rounded-xl hover:bg-brand-accent transition-all"
                >
                  {t('ld_login_to_collect')}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </>
            )}
            <div className="flex items-center gap-2 bg-white/5 rounded-xl px-4 py-3 text-sm">
              <ShieldCheck className="w-4 h-4 text-brand-success shrink-0" />
              <span className="text-warm-100/80">
                {t('ld_verified_visits')}
              </span>
            </div>
          </div>

          {/* Location details */}
          <div className="bg-white border border-warm-200/70 rounded-3xl p-7 space-y-6">
            <p className="font-heading font-bold text-xs tracking-[0.2em] text-warm-500 uppercase">
              {t('ld_location')}
            </p>

            {location.findInstructions && (
              <div className="space-y-2">
                <p className="font-heading font-bold text-[10px] tracking-[0.15em] text-warm-500 uppercase">
                  {t('location_find_host')}
                </p>
                <p className="text-warm-700 text-sm leading-relaxed">
                  {location.findInstructions}
                </p>
              </div>
            )}

            <div className="border-t border-warm-200 pt-5 space-y-5">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-warm-500 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div className="flex items-center gap-3">
                    <p className="font-heading font-bold text-[10px] tracking-[0.15em] text-warm-500 uppercase">
                      {t('ld_address')}
                    </p>
                    {fullAddress && <CopyButton value={fullAddress} />}
                  </div>
                  <p className="text-warm-700 text-sm">{fullAddress}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showClaim && (
        <ClaimOverlay
          locationId={location.id}
          locationName={location.name}
          region={location.region}
          category={location.category}
          stampId={location.stampId}
          stampName={location.stampName}
          onClose={() => setShowClaim(false)}
        />
      )}
    </div>
  );
};

export default LocationDetails;
